import type { ReactNode } from 'react';

import { LogoMark } from '@/components/LogoMark/LogoMark';

import styles from './AuthScreen.module.css';

export interface AuthScreenProps {
  /** Which deployment this build talks to, e.g. `staging`. */
  environment?: string | null;
  children: ReactNode;
}

const HOPS = [
  { service: 'turnstile', says: 'who you are' },
  { service: 'keystone', says: 'what you can see' },
  { service: 'bindery', says: 'what a file became' },
];

/**
 * The split sign-in layout: the form column on the left, a decorative panel on
 * the right that says what this place is. The panel drops away on narrow screens.
 */
export function AuthScreen({ environment, children }: AuthScreenProps) {
  return (
    <div className={styles.screen}>
      <div className={styles.column}>
        <div className={styles.brand}>
          <span className={styles.mark}>
            <LogoMark size={30} />
          </span>
          <span className={styles.wordmark}>LendWell</span>
          <span className={styles.badge}>Internal</span>
          {environment ? <span className={styles.env}>{environment}</span> : null}
        </div>
        <div className={styles.content}>{children}</div>
        <p className={styles.footnote}>
          A developer workbench, not the product. Engineering eyes only.
        </p>
      </div>

      <aside className={styles.art} aria-hidden="true">
        <div className={styles.glow} />
        <div className={styles.grid} />
        <div className={styles.artInner}>
          <span className={styles.artEyebrow}>Behind the curtain</span>
          <p className={styles.artTitle}>
            Every harness,
            <br />
            one round trip away.
          </p>
          <div className={styles.terminal}>
            <div className={styles.terminalBar}>
              <span />
              <span />
              <span />
              <em>portal — whoami</em>
            </div>
            <pre className={styles.terminalBody}>
              <span className={styles.prompt}>$</span> portal --whoami{'\n'}
              {HOPS.map(({ service, says }, index) => (
                <span
                  className={styles.hop}
                  key={service}
                  style={{ animationDelay: `${700 + index * 400}ms` }}
                >
                  <span className={styles.tick}>✓</span> {service.padEnd(10)}
                  <span className={styles.dim}>{says}</span>
                  {'\n'}
                </span>
              ))}
              <span className={styles.prompt}>$</span> <span className={styles.caret} />
            </pre>
          </div>
          <div className={styles.chips}>
            <span>Hello</span>
            <span>Files</span>
            <span>Templates</span>
          </div>
        </div>
      </aside>
    </div>
  );
}
